/**
 * Guided Flow - Main React Component
 *
 * Orchestrates the flow experience. No chrome — the chat IS the interface.
 * Navigation between steps happens through chat actions (navigate links).
 * Only a minimal progress bar and exit button are shown.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { GuidedFlowController } from '../core/GuidedFlowController';
import { FlowContext, FlowStep, FlowViewType } from '../core/types';
import { useFlowProviders } from '../providers';
import { FlowRenderer } from './FlowRenderer';
import { AppSidebar } from '@/components/sidebar/app-sidebar';
import { ChatPanel } from '@/components/ui/chat-panel';
import { useChatPanel } from '@/contexts/ChatPanelContext';
import { useDemoConversation } from '../contexts/DemoConversationContext';
import { useGuidedFlowLayoutChat } from '../contexts/GuidedFlowLayoutChatContext';
import { motion, AnimatePresence } from 'motion/react';
import type { Message } from '@/types/chat.types';

/** Map flow view components to their demo route so the sidebar highlights correctly */
const VIEW_TO_ROUTE: Partial<Record<FlowViewType, string>> = {
  'dashboard': '/demo/dashboard',
  'vault': '/demo/vault',
  'deal-creation': '/demo/new-deal',
  'deal-pipeline': '/demo/deal-pipeline',
  'chat-interface': '/demo/chat',
  'inbox': '/demo/inbox',
  'calendar': '/demo/calendar',
  'delivery-manager': '/demo/delivery-manager',
  'portfolio': '/demo/portfolio',
  'integrations': '/demo/integrations',
  'organizations': '/demo/org',
};

export interface GuidedFlowProps {
  flowId: string;
  autoStart?: boolean;
  embedded?: boolean;
  initialStepId?: string;
  onComplete?: () => void;
  onCancel?: () => void;
  className?: string;
}

export const GuidedFlow: React.FC<GuidedFlowProps> = ({
  flowId,
  autoStart = false,
  embedded = false,
  initialStepId,
  onComplete,
  onCancel,
  className = '',
}) => {
  const { providers } = useFlowProviders();
  const { openPanel } = useChatPanel();
  const { setLayoutDemoChat } = useGuidedFlowLayoutChat();
  const [controller] = useState(() => new GuidedFlowController(providers));
  const [context, setContext] = useState<FlowContext | null>(null);
  const [currentStep, setCurrentStep] = useState<FlowStep | null>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const handleFlowStart = () => {
      setContext(controller.getContext());
      setIsActive(true);
    };
    const handleFlowComplete = () => {
      setIsActive(false);
      onComplete?.();
    };
    const handleFlowCancel = () => {
      setIsActive(false);
      onCancel?.();
    };
    const handleStepChange = () => {
      setContext(controller.getContext());
      setCurrentStep(controller.getCurrentStep());
    };

    controller.on('flow:start', handleFlowStart);
    controller.on('flow:complete', handleFlowComplete);
    controller.on('flow:cancel', handleFlowCancel);
    controller.on('step:start', handleStepChange);

    return () => {
      controller.off('flow:start', handleFlowStart);
      controller.off('flow:complete', handleFlowComplete);
      controller.off('flow:cancel', handleFlowCancel);
      controller.off('step:start', handleStepChange);
    };
  }, [controller, onComplete, onCancel]);

  useEffect(() => {
    if (autoStart && !isActive) {
      const urlParams = new URLSearchParams(window.location.search);
      const stepParam = urlParams.get('step');

      controller.start(flowId).then(() => {
        const targetStep = stepParam ?? initialStepId;
        if (targetStep) {
          controller.goToStep(targetStep);
        }
      }).catch(err => {
        console.error('Failed to start flow:', err);
      });
    }
  }, [autoStart, flowId, controller, isActive, initialStepId]);

  // Set a CSS custom property so the sidebar can highlight the right nav item
  // without touching real routing
  useEffect(() => {
    const view = currentStep?.view;
    if (!view?.component) {
      document.documentElement.removeAttribute('data-demo-active-view');
      return;
    }

    let route = VIEW_TO_ROUTE[view.component];
    if (!route && view.component === 'deal-page' && view.props?.dealId) {
      route = `/demo/deal/${view.props.dealId}`;
    }
    if (!route && view.component === 'contract-analysis' && view.props?.contractId) {
      route = `/demo/analysis/${view.props.contractId}`;
    }

    if (route) {
      document.documentElement.setAttribute('data-demo-active-view', route);
    }

    return () => {
      document.documentElement.removeAttribute('data-demo-active-view');
    };
  }, [currentStep?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = useCallback(() => {
    controller.next().catch(console.error);
  }, [controller]);

  const handlePrevious = useCallback(() => {
    controller.previous().catch(console.error);
  }, [controller]);

  const handleSkip = useCallback(() => {
    controller.skip();
  }, [controller]);

  const handleCancel = useCallback(() => {
    controller.cancel();
  }, [controller]);

  // ── Persistent ChatPanel (outside page animations) ──────────────────
  const { messages: demoMessages, addMessages } = useDemoConversation();
  const [streamingMsg, setStreamingMsg] = useState<Message | null>(null);
  const lastStreamedStepRef = useRef<string | null>(null);

  const showDemoChat = currentStep?.type === 'view'
    && currentStep.view?.component !== 'chat-interface'
    && !!currentStep.view?.assistant?.message;

  // Stream the assistant message for the current view step
  useEffect(() => {
    if (!showDemoChat || !currentStep?.view?.assistant?.message) return;
    if (lastStreamedStepRef.current === currentStep.id) return;
    lastStreamedStepRef.current = currentStep.id;

    const fullText = currentStep.view.assistant.message;
    const msgId = `view-${currentStep.view.component}-${Date.now()}`;
    let cursor = 0;

    setStreamingMsg({ id: msgId, role: 'assistant', content: '', timestamp: new Date() });

    const interval = setInterval(() => {
      cursor = Math.min(cursor + 4, fullText.length);
      setStreamingMsg((prev) =>
        prev ? { ...prev, content: fullText.slice(0, cursor) } : null,
      );
      if (cursor >= fullText.length) {
        clearInterval(interval);
        addMessages([{ id: msgId, role: 'assistant', content: fullText, timestamp: new Date() }]);
        setStreamingMsg(null);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [currentStep?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const panelMessages: Message[] = useMemo(
    () => [
      ...demoMessages.map((m) => ({
        id: m.id, role: m.role as Message['role'], content: m.content, timestamp: m.timestamp,
      })),
      ...(streamingMsg ? [streamingMsg] : []),
    ],
    [demoMessages, streamingMsg],
  );

  useEffect(() => {
    if (!embedded || !isActive || !currentStep) {
      setLayoutDemoChat(null);
      return;
    }

    if (showDemoChat) {
      setLayoutDemoChat({
        demoMessages: panelMessages,
        demoAction: { label: 'Continue →', onClick: handleNext },
        demoLoading: !!streamingMsg && streamingMsg.content.length === 0,
      });
      openPanel();
      return () => setLayoutDemoChat(null);
    }

    setLayoutDemoChat(null);
    return () => setLayoutDemoChat(null);
  }, [
    embedded,
    isActive,
    currentStep?.id,
    showDemoChat,
    panelMessages,
    streamingMsg,
    handleNext,
    openPanel,
    setLayoutDemoChat,
  ]);

  if (!isActive || !context || !currentStep) {
    if (embedded && autoStart) {
      return <div className={`guided-flow-container flex h-full min-h-0 flex-col ${className}`} />;
    }
    return null;
  }

  const progress = ((context.currentStepIndex + 1) / context.totalSteps) * 100;

  const shellClassName = embedded
    ? `guided-flow-container flex h-full min-h-0 flex-col ${className}`
    : `guided-flow-container fixed inset-0 z-50 flex bg-sidebar ${className}`;

  const content = (
      <div className={embedded
        ? "flex h-full min-h-0 w-full flex-col overflow-hidden bg-background"
        : "flex h-full w-full flex-col overflow-hidden rounded-xl border border-border bg-background shadow-sm"
      }>

      {/* Top bar — progress + step controls */}
      <div className="relative z-10 shrink-0">
        <div className="h-0.5 bg-border">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <div className="flex items-center justify-between border-b border-border/50 px-4 py-1">
          {/* Left: step nav */}
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrevious}
              disabled={context.currentStepIndex === 0}
              className="flex items-center gap-0.5 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-default transition-colors"
            >
              <ChevronLeft className="size-3" />
              Back
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-0.5 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              Next
              <ChevronRight className="size-3" />
            </button>
          </div>

          {/* Center: step info */}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground/60">
            <span className="font-mono">{context.currentStepIndex + 1}/{context.totalSteps}</span>
            <span className="text-border">|</span>
            <span className="truncate max-w-48">{currentStep.title || currentStep.id}</span>
            <span className="text-border">|</span>
            <span className="font-mono text-muted-foreground/40">{currentStep.type}</span>
          </div>

          {/* Right: exit */}
          <button
            onClick={handleCancel}
            className="rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            Exit Demo
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep.id}
            initial={embedded ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={embedded ? undefined : { opacity: 0, y: -10 }}
            transition={{ duration: embedded ? 0 : 0.25 }}
            className="h-full"
          >
            <FlowRenderer
              step={currentStep}
              context={context}
              controller={controller}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onSkip={handleSkip}
              onCancel={handleCancel}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {!embedded && showDemoChat && (
        <ChatPanel
          demoMessages={panelMessages}
          demoAction={{ label: 'Continue →', onClick: handleNext }}
          demoLoading={!!streamingMsg && streamingMsg.content.length === 0}
        />
      )}

      </div>
  );

  if (embedded) {
    return <div className={shellClassName}>{content}</div>;
  }

  return (
    <div className={shellClassName}>
      <div className="pointer-events-none shrink-0 select-none">
        <AppSidebar isDemo />
      </div>
      <div className="relative flex-1 min-w-0 p-2 pl-0">
        {content}
      </div>
    </div>
  );
};
